/*!
 * Distance-capped lazy image loading.
 *
 * Why this exists: native <img loading="lazy"> defers the fetch until the
 * browser's own "distance from viewport" heuristic says so - and Chromium
 * *widens* that distance on slower effective connection types. On a long,
 * image-heavy page that means most below-fold images fall inside the
 * widened threshold at once on a throttled connection, so they all start
 * fetching in a burst that competes with the LCP image for bandwidth.
 *
 * This replaces that heuristic with a small, fixed IntersectionObserver
 * margin that does not change with network speed: images only start
 * fetching once they are genuinely about to enter the viewport.
 *
 * Markup contract for any <img> that should be distance-capped:
 *   <img data-src="..." data-srcset="..." loading="lazy" width="…" height="…">
 * (real src/srcset only ever added by this script, on intersection) plus a
 * <noscript> fallback with a real <img> for no-JS clients/crawlers.
 *
 * For <picture><source> variants (e.g. a mobile breakpoint image), also use
 * data-srcset on the <source> tags: <source media="..." data-srcset="...">.
 * This matters because the browser's picture resource-selection algorithm
 * can pick a matching <source>'s srcset independently of the <img>'s own
 * src - a real srcset left on a <source> would still get fetched under
 * Chrome's wide native-lazy distance even though the <img> itself has no
 * src, defeating the point of this script. Observing/activating always
 * happens on the <img>; activate() also updates any sibling <source
 * data-srcset> in the same <picture>.
 *
 * A24: images inside a [data-lazy-after-load] container (e.g. the home ingredient slider, whose cards
 * and their clones sit within the 500px look-ahead right below the hero) are held back until window
 * load or the first scroll/touch/key, so they don't share the connection with the LCP image. A held
 * image that is actually on screen (0px margin) still loads at once.
 *
 * window.YanaaDistanceLazyLoad.observe(root) lets scripts that clone or
 * inject markup after the initial page scan (e.g. carousels that
 * cloneNode() slides for an infinite-scroll buffer) register the new
 * images without waiting for a full re-scan.
 */
(function () {
  if (window.YanaaDistanceLazyLoad) return;

  var ROOT_MARGIN = '0px 0px 500px 0px';
  var observer = null;
  var visibleObserver = null;
  var released = document.readyState === 'complete';
  var held = [];
  var desktopIngredients = window.matchMedia('(min-width: 768px)');

  // Ingredient art is desktop-only. Leaving data-src in place means a phone
  // never requests those files. Crossing to a desktop width loads them.
  function skipOnThisScreen(img) {
    return img.hasAttribute('data-yanaa-desktop-only') && !desktopIngredients.matches;
  }

  function activate(img) {
    if (skipOnThisScreen(img)) return;
    var picture = img.parentElement && img.parentElement.tagName === 'PICTURE' ? img.parentElement : null;

    if (picture) {
      picture.querySelectorAll('source[data-srcset]').forEach(function (source) {
        source.setAttribute('srcset', source.getAttribute('data-srcset'));
        source.removeAttribute('data-srcset');
      });
    }

    var srcset = img.getAttribute('data-srcset');
    var src = img.getAttribute('data-src');

    if (srcset) {
      img.setAttribute('srcset', srcset);
      img.removeAttribute('data-srcset');
    }
    if (src) {
      img.setAttribute('src', src);
      img.removeAttribute('data-src');
    }
  }

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;

    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        activate(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: ROOT_MARGIN, threshold: 0.01 });

    return observer;
  }

  function getVisibleObserver() {
    if (visibleObserver || !('IntersectionObserver' in window)) return visibleObserver;

    visibleObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        activate(entry.target);
        visibleObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px', threshold: 0.01 });

    return visibleObserver;
  }

  function release() {
    if (released) return;
    released = true;
    var list = held;
    held = [];
    if (visibleObserver) visibleObserver.disconnect();
    var io = getObserver();
    list.forEach(function (img) {
      if (!img.isConnected || !(img.hasAttribute('data-src') || img.hasAttribute('data-srcset'))) return;
      if (io) io.observe(img);
      else activate(img);
    });
  }

  if (!released) {
    window.addEventListener('load', release, { once: true });
    ['scroll', 'touchstart', 'keydown', 'wheel'].forEach(function (type) {
      window.addEventListener(type, release, { once: true, passive: true });
    });
  }

  function observe(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var images = scope.querySelectorAll('img[data-src], img[data-srcset]');
    if (!images.length) return;

    var io = getObserver();

    images.forEach(function (img) {
      if (skipOnThisScreen(img)) return;
      if (!released && io && img.closest('[data-lazy-after-load]')) {
        held.push(img);
        getVisibleObserver().observe(img);
        return;
      }
      // No "already observed" dataset marker here on purpose: a marker set
      // via dataset survives cloneNode(true), so carousels that clone
      // already-bound slides (e.g. for an infinite-scroll buffer) produce
      // clones that look "already bound" despite never having been passed
      // to the observer themselves - their data-src/data-srcset then never
      // gets promoted to a real src, leaving a blank/alt-text image. Calling
      // IntersectionObserver#observe() again on a node that's genuinely
      // already being observed is a harmless no-op, so it's safe to just
      // always (re-)observe every match here.
      if (io) {
        io.observe(img);
      } else {
        // No IntersectionObserver support: load immediately rather than
        // leaving the image blank forever.
        activate(img);
      }
    });
  }

  desktopIngredients.addEventListener('change', function () {
    if (desktopIngredients.matches) observe(document);
  });

  window.YanaaDistanceLazyLoad = { observe: observe, activate: activate };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observe(document); });
  } else {
    observe(document);
  }
})();
